/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import BaseCom from '../components/BaseCom';
import FormInput from '../components/FormInput';
import L from '../components/Lang';
import { withParamsAndNavigate } from '../components/WithParamsAndNavigate';
import { qreq } from '../shared/qrequest';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import './ClientOnboarding.css';
import Auth from '../shared/Auth';

class ClientOnboarding extends BaseCom {
    constructor(props) {
        super(props);

        this.state = {
            info: {},
            otp: {},
            submitting: false,
            success: false,
            verified: false,
            resending: false
        };
    }

    async componentDidMount() {
        await this.getMainOrg((j) => {
            this.setState({ instanceError: j.errorMessage });
        });
        try {
            const user = await Auth.getLoginAsync();

            if (user) {
                window.location.assign(Auth.clientFollowPath);
                return;
            }
        }
        catch (e) {
            // Continue to login screen on auth check failure.
        }
        await this.checkSession();
    }

    checkSession = async () => {
        try {
            const j = await qreq.getAsync('/api/signup/session');
            if (j.item) {
                this.setState({
                    success: true,
                    verified: false,
                    newUser: j.item
                });
            }
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
    }

    change = () => {
        this.setState({ info: { ...this.state.info } });
    }

    changeOtp = () => {
        this.setState({ otp: { ...this.state.otp } });
    }

    setGender = (gender) => {
        this.setState({ info: { ...this.state.info, gender } });
    }

    toggleInfo = (name) => {
        this.setState({
            info: {
                ...this.state.info,
                [name]: !this.state.info[name]
            }
        });
    }

    submit = async (e) => {
        e?.preventDefault();

        if (!this.state.info.gender) {
            this.alert(<L>Please select a biological gender.</L>);
            return;
        }

        if (!this.state.info.consent) {
            this.alert(<L>You must consent to the Terms of Service and Privacy Policy.</L>);
            return;
        }

        this.setState({ submitting: true });

        const info = { ...this.state.info };

        try {
            const j = await qreq.postAsync('/api/signup/submit', info);

            if (j.errorCode === 0) {
                this.setState({
                    success: true,
                    verified: false,
                    newUser: j.item
                });
            }
            else {
                this.alert(j.errorMessage);
            }
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }

        this.setState({ submitting: false });
    }

    submitOtp = async (e) => {
        e?.preventDefault();

        const code = (this.state.otp.code || '').trim();

        if (!/^\d{6}$/.test(code)) {
            this.alert(<L>Please enter a valid 6 digit code.</L>);
            return;
        }

        this.setState({ submitting: true });

        try {
            const j = await qreq.postAsync('/api/signup/otp', { code });

            if (j.errorCode === 0) {
                this.setState({
                    verified: true,
                    success: false,
                    newUser: j.item
                });

                setTimeout(() => {
                    window.location.assign('/'); // redirect
                }, 1500);
            }
            else {
                this.alert(j.errorMessage);
            }
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }

        this.setState({ submitting: false });
    }

    resendOtp = async () => {
        if (this.state.resending)
            return;

        this.setState({ resending: true });

        try {
            const j = await qreq.postAsync('/api/signup/otp/resend');

            if (j.errorCode === 0) {
                this.alert(<L>A new code has been sent to your email.</L>);
            }
            else {
                this.alert(j.errorMessage);
            }
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }

        this.setState({ resending: false });
    }

    render() {
        return (
            <section className="client-onboarding-page">
                <div className="client-onboarding-bg" />

                <div className="container client-onboarding-container">
                    <div className="client-onboarding-shell">
                        <aside className="client-onboarding-hero">
                            <div className="client-onboarding-logo-wrap">
                                <img src="/img/logo-l.png" alt="" className="client-onboarding-logo" />
                            </div>

                            <div className="client-onboarding-hero-content">
                                <span className="client-onboarding-eyebrow"><L>Member Signup</L></span>
                                <h1 className="h1-hero"><L>Take control of your health journey.</L></h1>
                                <p>
                                    <L>From hydration to immunity, energy to recovery — stay ahead with a smarter health experience.</L>
                                </p>
                            </div>

                            <div className="client-onboarding-points">
                                <div>
                                    <Icon icon="faShield" />
                                    <span><L>Secure account setup</L></span>
                                </div>
                                <div>
                                    <Icon icon="faFlask" />
                                    <span><L>Wellness-ready profile</L></span>
                                </div>
                                <div>
                                    <Icon icon="faMobile" />
                                    <span><L>Updates by email or phone</L></span>
                                </div>
                            </div>
                        </aside>

                        <main className="client-onboarding-card">
                            {this.state.instanceError ? (
                                <Alert type="danger" message={this.state.instanceError} />
                            ) : this.state.verified ? (
                                <div className="client-onboarding-success">
                                    <div className="client-onboarding-success-icon">
                                        <Icon icon="faCheck" />
                                    </div>
                                    <h2><L>Account Verified</L></h2>
                                    <p><L>Your account is now active. Redirecting...</L></p>
                                </div>
                            ) : this.state.success ? (
                                <div className="client-onboarding-success">
                                    <form onSubmit={this.submitOtp}>
                                        <div className="d-flex justify-content-center">
                                            <div className="client-onboarding-success-icon">
                                                <Icon icon="faEnvelope" />
                                            </div>
                                        </div>
                                        <h2><L>One More Thing</L></h2>
                                        <p><L>You will receive a 6 digit code in your email.</L></p>
                                        <p><L>Please enter it below:</L></p>
                                        <FormInput
                                            model={this.state.otp}
                                            name="code"
                                            label="XXXXXX"
                                            labelAsPlaceholder
                                            type="text"
                                            inputClassName="mt-4 p-3 text-center"
                                            inputMode="numeric"
                                            maxLength={6}
                                            required
                                            onChange={this.changeOtp}
                                        />

                                        {this.state.error ? (
                                            <Alert message={this.state.error} type="danger" className="mt-3" />
                                        ) : ''}
                                        <div className="d-grid mt-4">
                                            <button type="submit" className="btn btn-success btn-lg client-onboarding-submit" disabled={this.state.submitting}>
                                                {this.state.submitting ? <>
                                                    <Icon icon="faRefresh" className="spinner" /> <L>Confirming...</L>
                                                </> : <>
                                                    <L>Confirm</L> <Icon icon="faArrowRight" />
                                                </>}
                                            </button>
                                        </div>

                                        <div className="text-center mt-3">
                                            <button type="button" className="btn btn-link" disabled={this.state.resending || this.state.submitting} onClick={this.resendOtp}>
                                                {this.state.resending ? <L>Sending...</L> : <L>Resend Code</L>}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <>
                                    <div className="client-onboarding-card-header">
                                        <h2><L>Create Your Account</L></h2>
                                        <p>
                                            <L>You'll need a valid phone number and email.</L>
                                        </p>
                                    </div>

                                    <form onSubmit={this.submit} className="client-onboarding-form">
                                        <div className="client-onboarding-section">
                                            <div className="client-onboarding-section-title">
                                                <span>1</span>
                                                <div>
                                                    <h3><L>Contact Information</L></h3>
                                                    <p><L>We’ll use this information to create your member profile.</L></p>
                                                </div>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <FormInput model={this.state.info} name="email" type="email" label="Email" placeholder="Email" labelAsPlaceholder onChange={this.change} autoComplete="email" required />
                                                </div>
                                                <div className="col-md-6">
                                                    <FormInput model={this.state.info} name="phone" type="text" label="Phone Number" placeholder="Phone Number" labelAsPlaceholder onChange={this.change} autoComplete="tel" required />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="client-onboarding-section">
                                            <div className="client-onboarding-section-title">
                                                <span>2</span>
                                                <div>
                                                    <h3><L>Full Legal Name</L></h3>
                                                    <p><L>Your name must exactly match the identification you present at each visit.</L></p>
                                                </div>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <FormInput model={this.state.info} name="firstName" type="text" label="First name" placeholder="First name" labelAsPlaceholder onChange={this.change} autoComplete="given-name" required />
                                                </div>
                                                <div className="col-md-6">
                                                    <FormInput model={this.state.info} name="lastName" type="text" label="Last name" placeholder="Last name" labelAsPlaceholder onChange={this.change} autoComplete="family-name" required />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="client-onboarding-section">
                                            <div className="client-onboarding-section-title">
                                                <span>3</span>
                                                <div>
                                                    <h3><L>Basic Profile</L></h3>
                                                    <p><L>This helps prepare your account for lab and wellness services.</L></p>
                                                </div>
                                            </div>

                                            <div className="row g-3 align-items-end">
                                                <div className="col-md-5">
                                                    <FormInput model={this.state.info} name="birthDate" type="date" label="Birth date" onChange={this.change} autoComplete="bday" required />
                                                </div>
                                                <div className="col-md-7">
                                                    <label className="form-label"><L>Biological Sex</L></label>
                                                    <div className="client-onboarding-choice-group mb-3">
                                                        <button type="button" className={'client-onboarding-choice ' + (this.state.info.gender === 'F' ? 'active' : '')} onClick={() => this.setGender('F')}>
                                                            <Icon icon="faVenus" /> <L>Female</L>
                                                        </button>
                                                        <button type="button" className={'client-onboarding-choice ' + (this.state.info.gender === 'M' ? 'active' : '')} onClick={() => this.setGender('M')}>
                                                            <Icon icon="faMars" /> <L>Male</L>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="client-onboarding-section">
                                            <div className="client-onboarding-section-title">
                                                <span>4</span>
                                                <div>
                                                    <h3><L>Consent</L></h3>
                                                    <p><L>Please review and confirm your communication and service preferences.</L></p>
                                                </div>
                                            </div>

                                            <div className="client-onboarding-consent">
                                                <div className="client-onboarding-switch">
                                                    <FormInput model={this.state.info} name="consentMarketing" type="switch" label="" onChange={this.change} />
                                                </div>
                                                <div role="button" onClick={() => this.toggleInfo('consentMarketing')}>
                                                    <strong><L>Marketing Messages</L></strong>
                                                    <p><L>I agree to receive marketing messages from {this.state.org?.name} via automated text messages at the phone number provided. Consent is not required to use this service. Reply STOP to opt out. Message and data rates may apply.</L></p>
                                                </div>
                                            </div>

                                            <div className="client-onboarding-consent">
                                                <div className="client-onboarding-switch">
                                                    <FormInput model={this.state.info} name="consent" type="switch" label="" onChange={this.change} />
                                                </div>
                                                <div role="button" onClick={() => this.toggleInfo('consent')}>
                                                    <strong><L>Terms & Privacy</L></strong>
                                                    <p><L>I agree to</L> {this.state.org?.name} <span><L>Terms of Service</L></span> <L>and</L> <span><L>Privacy Policy</L></span>.</p>
                                                </div>
                                            </div>
                                        </div>

                                        {this.state.error ? (
                                            <Alert message={this.state.error} type="danger" className="mt-3" />
                                        ) : ''}

                                        <div className="d-grid mt-4">
                                            <button type="submit" className="btn btn-success btn-lg client-onboarding-submit" disabled={this.state.submitting}>
                                                {this.state.submitting ? <>
                                                    <Icon icon="faRefresh" className="spinner" /> <L>Submitting...</L>
                                                </> : <>
                                                    <L>Continue</L> <Icon icon="faArrowRight" />
                                                </>}
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </main>
                    </div>
                </div>
            </section>
        );
    }
}

export default withParamsAndNavigate(ClientOnboarding);
